import React, { useEffect, useState } from "react";
import useEditLoad from "../../hooks/useEditLoad";
import EditLoadGate from "../../components/editLoadGate";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Select from "../../components/select";
import { AsyncSelect } from "../../components/select";

import noImage from "../../images/no-image.png";
import { uploadMediaFile } from "../../utills/fileUpload";
import { useLanguage } from "../../context/languageContext";
import PreviewImage from "../../components/image/previewImage";
import Button from "../../components/button";
import LanguageSelector from "../../components/languageSelector";
import {
    fetchAllBookCategoriesByLangOptions,
    fetchAllGenresByLangOptions,
    normalizeCoverImage,
    pastePlainText,
} from "../../helper";
import { mergePickedBooks, searchEmptyMessage } from "./seriesBooks";
import SortablePeopleList from "../../components/sortablePeople";
import { usePermissions } from "../../context/permissionContext";
import { useAlreadyExists } from "../../components/confirmDialog";
import { rankMatches } from "../../helper/searchFold";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Add / edit a series — an ordered set of books that belong together.
 *
 * The same form as a collection's, because they carry the same fields, pointed
 * at the series endpoints. What it does NOT have is bulk CSV upload: a series
 * is a reading order somebody decides on, not a list to import.
 *
 * Genres are derived from the books rather than typed, exactly as collections
 * does it — the field is read-only and refills whenever the book list changes.
 */
const SeriesPopup = ({ isVisible, seriesId, handleCloseModal, onSuccess }) => {
    const { submitLabel } = usePermissions();
    const { languages, selectedLang, setSelectedLang, adminInfo, initialLangRef, fallbackLang } = useLanguage();
    const showAlreadyExists = useAlreadyExists();
    const { t } = useTranslation();

    const isEditing = Boolean(seriesId);

    const createInitialLangValues = (lang) => ({
        langCode: lang,
        title: "",
        description: "",
        books: [],
        genres: [],
    });

    const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

    const [values, setValues] = useState({
        coverImage: {},
        translations: createInitialTranslationsArr(),
    });
    const [particularSeriesData, setParticularSeriesData] = useState(null);
    const [bookOptions, setBookOptions] = useState([]);
    const [genreOptions, setGenreOptions] = useState([]);
    const [editMediaError, setEditMediaError] = useState("");
    const [errors, setErrors] = useState({});
    const [isSubmit, setIsSubmit] = useState(false);
    // Edit shows, and saves, only the series that was opened — see hooks/useEditLoad.
    const load = useEditLoad();
    const editReady = !isEditing || Boolean(isVisible && load.isLoadedFor(seriesId));

    useEffect(() => {
        if (!isVisible) {
            load.reset();
            return;
        }
        const lang = fallbackLang || selectedLang;
        if (fallbackLang) setSelectedLang(fallbackLang);
        if (isEditing) {
            load.begin(seriesId);
            initialLangRef.current = false;
            handleFetchParticularSeries(String(seriesId), lang);
        } else {
            fetchAllBooksData(lang);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, seriesId]);

    const handleFetchParticularSeries = async (id, lang) => {
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${API}/api/admin/series/get/particular/${id}`, { headers });
            if (response?.status && load.accepts(id, response.data)) {
                setParticularSeriesData(response.data);
                await fetchAllBooksData(lang);
            } else {
                load.fail(id, response?.message || "");
            }
        } catch (error) {
            console.error("handleFetchParticularSeries catch error::", error);
            load.fail(id, error);
        }
    };

    useEffect(() => {
        if (particularSeriesData && particularSeriesData.translations && !initialLangRef.current) {
            const mergedTranslations = languages.map(
                (lang) =>
                    particularSeriesData.translations.find((tr) => tr.langCode === lang) ||
                    createInitialLangValues(lang)
            );

            setValues((prevValues) => ({
                ...prevValues,
                coverImage: normalizeCoverImage(particularSeriesData.coverImage),
                translations: mergedTranslations,
            }));

            initialLangRef.current = true;
            load.loaded(particularSeriesData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [particularSeriesData, languages]);

    const fetchAllBooksData = async (lang) => {
        // The language is passed in rather than read from state: the effect that
        // opens this form calls `setSelectedLang(fallbackLang)` and then fetches
        // in the same pass, so state still holds the language the form was
        // showing last time and the picker would list book names in it.
        const langCode = lang || selectedLang;

        // Every book, not only the summarised ones.
        //
        // A collection restricts itself to books with a published summary
        // because its tiles open the summary reader. A series is the books
        // themselves — the order an author wrote them in — and most of a series
        // will not have been summarised, so that filter left the picker unable
        // to find the very titles a series is made of.
        const books = await fetchAllBookCategoriesByLangOptions(langCode, adminInfo.token);
        const genres = await fetchAllGenresByLangOptions(langCode, adminInfo.token);

        setBookOptions(
            books.map((book) => ({
                label: book.bookName,
                value: book.bookId,
                authorName: book.authorName,
                image: book.image,
            }))
        );
        setGenreOptions(genres.map((genre) => ({ label: genre.genreName, value: genre.genreId, type: genre.type })));
    };

    const handleGetTranslationByLang = (langCode) =>
        values.translations?.find((lang) => lang.langCode === langCode) || {};

    const handleUpdateTranslationField = async (fieldName, value, langCode) => {
        setValues((prevValues) => ({
            ...prevValues,
            translations: prevValues.translations?.map((translation) =>
                translation.langCode === langCode ? { ...translation, [fieldName]: value } : translation
            ),
        }));

        if (fieldName === "books") await handleUpdateGenresByBooks(value, langCode);
    };

    const handleSelectLanguage = (language) => {
        setSelectedLang(language);
        // Book and genre names are per-language, so the pickers reload with it.
        fetchAllBooksData(language);
        setValues((prevValues) =>
            prevValues.translations.some((lang) => lang.langCode === language)
                ? { ...prevValues }
                : { ...prevValues, translations: [...prevValues.translations, createInitialLangValues(language)] }
        );
    };

    // Books as options for both the picker and the grid, in stored order. The
    // cover comes from the full book list loaded once above; the series itself
    // only stores id and name.
    const bookOptionsForLang = (handleGetTranslationByLang(selectedLang)?.books || []).map((book) => {
        const known = bookOptions.find((b) => b.value === book.bookId);
        return {
            value: book.bookId,
            label: known?.authorName ? `${book.bookName} — ${known.authorName}` : book.bookName,
            image: known?.image,
        };
    });

    // Every book stays searchable: one already in the series is shown dimmed
    // and tagged "Already added" by the picker's `markAdded`, rather than
    // vanishing from the results as if the library didn't have it.
    const searchableBooks = bookOptions;

    const handleBooksPicked = (options) => {
        const current = handleGetTranslationByLang(selectedLang)?.books || [];
        handleUpdateTranslationField("books", mergePickedBooks(current, options), selectedLang);
    };

    // The grid hands back the exact order it wants — from a drag, the position
    // box or the move buttons — so it is taken literally. Reordering by id keeps
    // the stored bookName rather than the option's label, which has the author
    // appended for the picker's benefit.
    const handleBooksReordered = (options) => {
        const current = handleGetTranslationByLang(selectedLang)?.books || [];
        const byId = new Map(current.map((b) => [b.bookId, b]));
        handleUpdateTranslationField(
            "books",
            (options || []).map((o) => byId.get(o.value)).filter(Boolean),
            selectedLang
        );
    };

    const handleUpdateGenresByBooks = async (value, language) => {
        try {
            const headers = { Authorization: adminInfo.token ? `Bearer ${adminInfo.token}` : null };
            const { data: response } = await axios.post(
                `${API}/api/admin/books/genres-by-books`,
                { value, language },
                { headers }
            );

            if (response.status) {
                const genresByBooks = (response?.data || []).map((genre) => ({
                    genreName: genre.genreName,
                    genreId: genre.genreId,
                    type: genre.type,
                }));

                setValues((prevValues) => ({
                    ...prevValues,
                    translations: prevValues.translations.map((translation) =>
                        translation.langCode === language ? { ...translation, genres: genresByBooks } : translation
                    ),
                }));
            }
        } catch (error) {
            console.error("handleUpdateGenresByBooks catch error...", error);
        }
    };

    const handleUploadMedia = async (event) => {
        const result = await uploadMediaFile(event, true);
        if (typeof result === "string") {
            setEditMediaError(result);
        } else if (typeof result === "object" && result.length > 0) {
            setEditMediaError("");
            setValues((prevValues) => ({ ...prevValues, coverImage: result[0]?.data }));
        }
    };

    const handleClose = () => {
        handleCloseModal();
        setErrors({});
        setEditMediaError("");
        setParticularSeriesData(null);
        setValues({ coverImage: {}, translations: createInitialTranslationsArr() });
    };

    const validateFields = (value) => {
        const newErrors = {};
        const translation = value?.translations?.find((tr) => tr.langCode === selectedLang) || {};

        if (!translation.title || translation.title.trim() === "") {
            newErrors.title = "Please enter a name for the series";
        }
        if (!translation.description || translation.description.trim() === "") {
            newErrors.description = "Please provide the description of the series";
        }
        if (!translation.books || translation.books.length === 0) {
            newErrors.books = "Please add at least one book";
        }

        return newErrors;
    };

    const handleAddAndUpdateValues = async () => {
        // Never save an edit form that is not showing this series.
        if (!editReady) return;
        const newErrors = validateFields(values);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const url = isEditing
                ? `${API}/api/admin/series/update/${seriesId}`
                : `${API}/api/admin/series/add`;

            const { data: response } = await axios[isEditing ? "put" : "post"](url, { values }, { headers });

            if (response.status) {
                setErrors({});
                toast.success(response?.message);
                onSuccess();
                handleClose();
            } else {
                toast.error(response?.error || "Could not save this series.");
            }
        } catch (error) {
            console.error("handleAddAndUpdateValues catch error::", error);
            if (!showAlreadyExists(error)) toast.error(error.response?.data?.error || "Could not save this series.");
        } finally {
            setIsSubmit(false);
        }
    };

    const BookOption = (props) => {
        const { data, innerRef, innerProps, className, isFocused, isSelected } = props;
        const backgroundColor = isSelected ? "var(--ps-green)" : isFocused ? "var(--ps-surface-2)" : "transparent";
        const borderColor = isSelected ? "var(--ps-green)" : isFocused ? "var(--ps-border)" : "transparent";
        const textColor = isSelected ? "var(--ps-green-ink)" : "var(--ps-text)";

        return (
            <div
                ref={innerRef}
                {...innerProps}
                className={`d-flex align-items-center gap-3 py-2 px-3 ${className || ""}`}
                style={{
                    cursor: "pointer",
                    backgroundColor,
                    borderRadius: 8,
                    border: `1px solid ${borderColor}`,
                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                    overflow: "hidden",
                }}
            >
                <div
                    className="flex-shrink-0 overflow-hidden"
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: "1px solid var(--ps-border)",
                        backgroundColor: "var(--ps-surface-2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img
                        src={data.image || noImage}
                        alt={data.label}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                </div>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-medium text-truncate" style={{ color: textColor }}>
                        {data.label}
                    </div>
                    {data.authorName && (
                        <div
                            className="small text-truncate"
                            style={{ color: isSelected ? "rgba(13, 13, 13, 0.72)" : "var(--ps-text-muted)" }}
                        >
                            {data.authorName}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isVisible}
            onRequestClose={isSubmit ? undefined : handleClose}
            className="modal_body_md"
            overlayClassName="modal_overlay"
        >
            <div className="box_wrapper">
                <div className="d-flex justify-content-between align-items-center fw-bold gap-2 mb-3">
                    <h4 className="mb-0">{isEditing ? "Edit Series" : "Add Series Collection"}</h4>
                    {isEditing && <LanguageSelector onSelectLanguage={handleSelectLanguage} />}
                </div>

                <EditLoadGate ready={editReady} error={load.loadError}>
                <div className="modal_content_lg">
                    <div className="row my-2">
                        <div className="col-sm-12 mb-3">
                            <label className="label">Series name:</label>
                            <input
                                className="form_control"
                                placeholder="Enter the series name..."
                                type="text"
                                name="title"
                                value={handleGetTranslationByLang(selectedLang).title || ""}
                                onChange={(e) =>
                                    handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)
                                }
                            />
                            {errors?.title && <p className="error-message d-block">{errors.title}</p>}
                        </div>

                        <div className="col-sm-12 mb-3">
                            <label className="label">Books:</label>
                            {/* The picker only adds. The grid below owns order and removal, so
                                the picker's own chips are hidden — two places to remove a book
                                from is one too many, which is also why its clear-all cross and
                                backspace are off: in a field that renders no value, both delete
                                rows with nothing on screen saying they had a target. */}
                            <AsyncSelect
                                isMulti
                                markAdded
                                isClearable={false}
                                backspaceRemovesValue={false}
                                controlShouldRenderValue={false}
                                placeholder="Search books by title..."
                                name="books"
                                value={bookOptionsForLang}
                                loadOptions={(inputValue, callback) => {
                                    // Best match first: exact title, starts with, word starts with, the rest.
                                    callback(rankMatches(searchableBooks, inputValue || "").slice(0, 100));
                                }}
                                defaultOptions={searchableBooks.slice(0, 100)}
                                components={{ Option: BookOption }}
                                onChange={handleBooksPicked}
                                // No `cacheOptions`: the pool is in memory, so
                                // filtering costs nothing — and a cached result
                                // for a term would still list a book picked
                                // since, which is exactly what must not show.
                                isSearchable
                                noOptionsMessage={({ inputValue }) =>
                                    searchEmptyMessage(
                                        inputValue,
                                        handleGetTranslationByLang(selectedLang)?.books || []
                                    )
                                }
                            />
                            <div className="mt-2">
                                <SortablePeopleList
                                    items={bookOptionsForLang}
                                    onChange={handleBooksReordered}
                                    thumbShape="cover"
                                    variant="grid"
                                    emptyMessage="No books yet — search above to add some."
                                />
                            </div>
                            <small className="text-muted">
                                A series is a reading order, so this order is the one the app shows.
                                Drag to reorder, or type a position to move a book straight there.
                            </small>
                            {errors?.books && <p className="error-message d-block">{errors.books}</p>}
                        </div>

                        <div className="col-sm-12 col-md-6 mb-3">
                            <label className="label">{t("general.genre")}:</label>
                            <Select
                                isMulti
                                isDisabled
                                placeholder="Filled in from the books..."
                                name="genre"
                                value={
                                    handleGetTranslationByLang(selectedLang)?.genres?.map((option) => ({
                                        label: option.genreName,
                                        value: option.genreId,
                                    })) || []
                                }
                                options={genreOptions}
                            />
                            <small className="text-muted">Taken from the books you add.</small>
                        </div>

                        <div className="col-sm-12 col-md-8 mb-3">
                            <label className="label">Description:</label>
                            <textarea
                                className="form_control"
                                placeholder="Enter the description..."
                                name="description"
                                value={handleGetTranslationByLang(selectedLang).description || ""}
                                onChange={(e) =>
                                    handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)
                                }
                                style={{ height: "260px" }}
                            // Pasted markdown markers ("**The Internet of Money**") are stripped:
                            // this field stores plain text, so they would be saved and shown
                            // literally.
                            onPaste={(event) =>
                              pastePlainText(event, (value) =>
                                handleUpdateTranslationField("description", value, selectedLang)
                              )
                            }
                            />
                            {errors?.description && <p className="error-message d-block">{errors.description}</p>}
                        </div>

                        <div className="col-sm-12 col-md-4">
                            <PreviewImage
                                label={t("general.coverImage")}
                                uploadMedia={(e) => handleUploadMedia(e)}
                                // A cover is optional: without one the app draws
                                // the first book's, so the form shows that rather
                                // than an empty box, which reads as a fault.
                                // Preview only — nothing is written, and the
                                // moment a cover is uploaded it wins here too.
                                image={values.coverImage?.uploadedImageUrl || bookOptionsForLang[0]?.image}
                                note={
                                    !values.coverImage?.uploadedImageUrl && bookOptionsForLang[0]?.image
                                        ? "Using the first book's cover — upload one to give the series its own."
                                        : null
                                }
                                mediaError={editMediaError}
                                error={errors?.coverImage}
                            />
                        </div>
                    </div>
                </div>
                </EditLoadGate>

                <div className="d-flex gap-2 justify-content-end mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>
                        Cancel
                    </button>
                    <Button
                        onClick={handleAddAndUpdateValues}
                        disabled={isSubmit || !editReady}
                        isSubmit={isSubmit}
                        label="Submitting ..."
                        text={isEditing ? "Update" : submitLabel("series")}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default SeriesPopup;
